import { useState } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import ColumnVisibilityMenu from 'src/components/ColumnVisibilityMenu';
import ListActionBar from 'src/components/ListActionBar';
import { useColumnVisibility } from 'src/hooks/columnVisibility';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useInfiniteWin32AppFiles } from 'src/hooks/wdac';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { getCustomerEnvironmentApplicationSourceOptions } from 'src/utils/enums/customerEnvironmentApplicationSource';
import Win32FileTable, {
  DEFAULT_FILE_SORT,
  DEFAULT_VISIBLE_WIN32_FILE_COLUMNS,
  SIGNED_FILTER_OPTION,
  WIN32_FILE_COLUMNS,
  toIsSigned,
  toggleFileSort,
} from '../Win32FileTable';
import Win32FileDetailModal from '../Win32FileDetailModal';

// Every file here is in an enabled set, so there is no coverage to show.
const FILES_TAB_COLUMNS = WIN32_FILE_COLUMNS.filter(column => column.key !== 'isCovered');

// With no import type checked every enabled import is listed, as before the filter existed.
const DEFAULT_FILES_FILTERS = { sources: [], isSigned: [] };
const FILES_FILTER_OPTIONS = [
  {
    key: 'sources',
    type: 'checkbox',
    label: 'Import type',
    options: getCustomerEnvironmentApplicationSourceOptions(),
  },
  SIGNED_FILTER_OPTION,
];

// The distinct files across the enabled file sets, which is what a generated catalog covers.
const FilesTab = () => {
  const { appId } = useParams();
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILES_FILTERS, FILES_FILTER_OPTIONS) ?? DEFAULT_FILES_FILTERS,
  );
  const [sort, setSort] = useState(DEFAULT_FILE_SORT);
  const [openFileId, setOpenFileId] = useState(null);
  const { visibleColumnDefinitions, isColumnVisible, setColumnVisible, resetColumns } =
    useColumnVisibility('win32AppFiles', FILES_TAB_COLUMNS, DEFAULT_VISIBLE_WIN32_FILE_COLUMNS);
  const trimmedSearch = searchValue.trim();

  useFilterParams(filters);

  const filesQuery = useInfiniteWin32AppFiles({
    globalApplicationVersionId: appId,
    sources: filters.sources,
    isSigned: toIsSigned(filters.isSigned),
    search: trimmedSearch,
    sortColumn: sort.column,
    sortDesc: sort.direction === 'desc',
    limit: EXPANDED_ROWS_LIMIT,
  });

  const rows = filesQuery.data?.pages?.flatMap(page => page.results || []) || [];
  const totalCount = filesQuery.data?.pages?.[0]?.totalCount;

  const [sentryRef] = useInfiniteScroll({
    loading: filesQuery.isFetchingNextPage,
    hasNextPage: !!filesQuery.hasNextPage,
    onLoadMore: filesQuery.fetchNextPage,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <Box>
      <ListActionBar
        onSearch={setSearchValue}
        searchPlaceholder="Search by path, file name, SHA-256 or Authenticode hash..."
        filters={filters}
        onFilterChange={next =>
          setFilters({ sources: next.sources ?? [], isSigned: next.isSigned ?? [] })
        }
        filterOptions={FILES_FILTER_OPTIONS}
        filterChips
        filterButtonCount={totalCount}
        actionArea={
          <ColumnVisibilityMenu
            columns={FILES_TAB_COLUMNS}
            isVisible={isColumnVisible}
            onChange={setColumnVisible}
            onReset={resetColumns}
          />
        }
        actionAreaContainerProps={{ flexShrink: 0 }}
      />
      <Win32FileTable
        rows={rows}
        columns={visibleColumnDefinitions}
        isLoading={filesQuery.isLoading}
        isFetchingNextPage={filesQuery.isFetchingNextPage}
        lastElementRef={sentryRef}
        searchValue={trimmedSearch}
        sort={sort}
        onSort={sortKey => setSort(previous => toggleFileSort(previous, sortKey))}
        onRowClick={row => setOpenFileId(row.fileId)}
        emptyText="No files are in this app's enabled file set imports."
        maxHeight="calc(100vh - 372px)"
      />
      <Win32FileDetailModal
        open={!!openFileId}
        onClose={() => setOpenFileId(null)}
        globalApplicationVersionId={appId}
        fileId={openFileId}
      />
    </Box>
  );
};

export default FilesTab;
